import Phaser from 'phaser';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { AudioManager } from '@/utils/AudioManager';

/**
 * Canonical CTA button — the SINGLE pill used for every CTA across the game.
 * One silhouette (the cyan menu PLAY pill), one centering rule, one set of
 * paddings. Hierarchy comes from SIZE, not color.
 *
 *   • 'primary'   — cyan PLAY pill (uses `menu_btn_rectangular` PNG)
 *   • 'secondary' — same cyan PLAY pill, no visual difference (size carries
 *                   hierarchy). Kept as a separate variant so call sites
 *                   can still mark their intent.
 *   • 'danger'    — coral code-drawn pill for destructive actions only.
 *
 * Sizes: 'small' (200×52 / 22px), 'medium' (320×72 / 30px), 'large' (360×110 / 48px).
 * Labels iteratively shrink fontSize when they overflow — never setScale,
 * which would distort the type.
 */
export type PillButtonVariant = 'primary' | 'secondary' | 'danger';
export type PillButtonSize = 'small' | 'medium' | 'large';

export interface PillButtonOptions {
  x: number;
  y: number;
  label: string;
  variant?: PillButtonVariant;
  size?: PillButtonSize;
  width?: number;
  height?: number;
  fontSize?: number;
  /**
   * When true (default), the SVG pill is drawn as a 3-slice so the end-caps
   * stay perfect semicircles (radius h/2) at any width instead of stretching
   * into ellipses. Set false to keep the legacy single-stretch render (used
   * only by the home screen PLAY button, which is intentionally left as-is).
   */
  roundedCaps?: boolean;
  onClick: () => void;
}

interface SizePreset {
  w: number;
  h: number;
  fontSize: number;
  paddingX: number;
  radius: number;
  strokeThickness: number;
}

const SIZES: Record<PillButtonSize, SizePreset> = {
  small:  { w: 200, h: 52,  fontSize: 22, paddingX: 18, radius: 14, strokeThickness: 2 },
  medium: { w: 320, h: 72,  fontSize: 30, paddingX: 24, radius: 14, strokeThickness: 3 },
  large:  { w: 360, h: 110, fontSize: 48, paddingX: 30, radius: 16, strokeThickness: 4 },
};

export function createPillButton(scene: Phaser.Scene, opts: PillButtonOptions): Phaser.GameObjects.Container {
  const variant: PillButtonVariant = opts.variant ?? 'primary';
  const size: PillButtonSize = opts.size ?? 'large';
  const preset = SIZES[size];

  const w = opts.width ?? preset.w;
  const h = opts.height ?? preset.h;
  const initialFontSize = opts.fontSize ?? preset.fontSize;

  const container = scene.add.container(opts.x, opts.y);

  // ── Background ──────────────────────────────────────────────────────
  // Uses the supplied vector button art: big-button.svg for the LARGE size,
  // button.svg for every other size. Falls back to the code-drawn pill if the
  // texture has not loaded yet, and for the coral danger variant.
  const radius = h / 2; // full radius => stadium / pill shape
  const roundedCaps = opts.roundedCaps ?? true;
  const svgKey = size === 'large' ? 'btn_svg_large' : 'btn_svg_regular';
  // Rendered cap radii of the silhouette. With round caps (the default) the pill
  // is a true stadium, so both radii are h/2 and selection rings sit parallel.
  // The legacy single-stretch render squashes the caps into ellipses (full
  // height vertically, only (w/2)·(texH/texW) horizontally).
  let capRx = radius;
  let capRy = radius;
  if (variant !== 'danger' && scene.textures.exists(svgKey)) {
    if (roundedCaps) {
      addRoundCapPill(scene, container, svgKey, w, h);
    } else {
      const bg = scene.add.image(0, 0, svgKey).setOrigin(0.5);
      bg.setDisplaySize(w, h);
      container.add(bg);
      const src = scene.textures.get(svgKey).getSourceImage();
      capRx = (w / 2) * (src.height / src.width);
      capRy = h / 2;
    }
  } else {
    const g = scene.add.graphics();
    if (variant === 'danger') drawDangerPill(g, w, h, radius);
    else drawCyanPill(g, w, h, radius);
    container.add(g);
  }

  // ── Label (centered on the pill, fits within paddingX) ─
  const labelStyle = labelStyleFor(variant, initialFontSize, preset.strokeThickness);
  const labelY = 0; // code-drawn pill is centered, no PNG shadow-band offset
  const txt = scene.add.text(0, labelY, opts.label, labelStyle as any)
    .setOrigin(0.5, 0.5)
    .setLetterSpacing(2);

  const maxLabelWidth = w - preset.paddingX * 2;
  let fs = initialFontSize;
  while (txt.width > maxLabelWidth && fs > 12) {
    fs -= 2;
    txt.setFontSize(fs);
  }
  container.add(txt);

  // ── Interactivity ──────────────────────────────────────────────────
  // Set hit area on the CONTAINER itself. Phaser's Container input quirks:
  // when `setSize` is called BEFORE `setInteractive`, the container's
  // display origin gets shifted to (w/2, h/2) and the hit-area Rectangle
  // is then ALSO offset by (-w/2, -h/2) when computing local coordinates —
  // double-shift bug. The reliable pattern is: provide a Rectangle in
  // local coords starting at (-w/2, -h/2) and DO NOT call setSize, so the
  // container's origin stays at (0, 0) and the rect is interpreted
  // verbatim around the container's anchor.
  const hitPad = 18;
  const hitW = w + hitPad * 2;
  const hitH = h + hitPad * 2;
  container.setInteractive(
    new Phaser.Geom.Rectangle(-hitW / 2, -hitH / 2, hitW, hitH),
    Phaser.Geom.Rectangle.Contains,
  );
  (container.input as Phaser.Types.Input.InputConfiguration).cursor = 'pointer';

  container.on('pointerover', () => {
    scene.tweens.add({ targets: container, scale: 1.05, duration: 140, ease: 'Back.easeOut' });
    AudioManager.getInstance().playSFX(scene,
      scene.cache.audio.exists('sfx_btn_hover') ? 'sfx_btn_hover' : 'sfx_btn_hover_wood',
      { volume: 0.35 });
  });
  container.on('pointerout', () => {
    scene.tweens.add({ targets: container, scale: 1, duration: 120, ease: 'Power2.easeIn' });
  });
  const press = () => {
    AudioManager.getInstance().playSFX(scene,
      scene.cache.audio.exists('sfx_btn_click_heavy') ? 'sfx_btn_click_heavy' : 'sfx_btn_click',
      { volume: 0.5 });
    scene.tweens.add({ targets: container, scale: 0.96, duration: 70, yoyo: true, ease: 'Power2.easeIn' });
    scene.time.delayedCall(110, () => opts.onClick());
  };
  container.on('pointerdown', press);

  // Expose computed dimensions + programmatic press hook
  // (used by keyboard shortcuts and GamepadNav).
  (container as any).pillWidth = w;
  (container as any).pillHeight = h;
  (container as any).pillCapRx = capRx;
  (container as any).pillCapRy = capRy;
  (container as any).press = press;

  return container;
}

// ─── Internals ──────────────────────────────────────────────────────────────

// Frame names carved onto the SVG button textures for 3-slice rendering.
const PILL_FRAME_L = '__pill_cap_left';
const PILL_FRAME_M = '__pill_mid';
const PILL_FRAME_R = '__pill_cap_right';

/**
 * Draw the pill background as a 3-slice so the caps never stretch.
 *
 * The button SVG is a true pill in its texture (cap radius = texHeight/2). We
 * carve it into left-cap | middle | right-cap, scale the two caps UNIFORMLY so
 * they render as perfect semicircles of radius h/2, and stretch ONLY the middle
 * strip horizontally to fill the requested width. Same art, round caps at any
 * width. The middle underlaps each cap by 1px (on the caps' full-height inner
 * edge, never the transparent rounded corners) to hide sub-pixel seams.
 */
function addRoundCapPill(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  svgKey: string,
  w: number,
  h: number,
): void {
  const tex = scene.textures.get(svgKey);
  const src = tex.getSourceImage() as HTMLImageElement;
  const texW = src.width;
  const texH = src.height;
  const capTex = texH / 2; // cap region width in texture px (true-pill radius)

  if (!tex.has(PILL_FRAME_L)) {
    tex.add(PILL_FRAME_L, 0, 0, 0, capTex, texH);
    tex.add(PILL_FRAME_M, 0, capTex, 0, texW - capTex * 2, texH);
    tex.add(PILL_FRAME_R, 0, texW - capTex, 0, capTex, texH);
  }

  const capDisp = h / 2; // round cap radius => uniform scale (capDisp/capTex == h/texH)

  const mid = scene.add.image(0, 0, svgKey, PILL_FRAME_M).setOrigin(0.5, 0.5);
  mid.setDisplaySize(Math.max(w - capDisp * 2, 0) + 2, h);

  const left = scene.add.image(-w / 2, 0, svgKey, PILL_FRAME_L).setOrigin(0, 0.5);
  left.setDisplaySize(capDisp, h);

  const right = scene.add.image(w / 2, 0, svgKey, PILL_FRAME_R).setOrigin(1, 0.5);
  right.setDisplaySize(capDisp, h);

  // Middle first, caps on top so the 1px overlap is covered by the opaque caps.
  container.add([mid, left, right]);
}

function labelStyleFor(variant: PillButtonVariant, fontSize: number, strokeThickness: number) {
  if (variant === 'danger') {
    return {
      fontFamily: FONTS.displayHeavy,
      fontSize: `${fontSize}px`,
      color: COLORS_HEX.pearlWhite,
      stroke: COLORS_HEX.midnightDepth,
      strokeThickness,
      shadow: { offsetX: 0, offsetY: 2, color: COLORS_HEX.midnightDepth, blur: 4, fill: true },
    };
  }
  // primary + secondary share the cyan PLAY pill — Skranji headline in the
  // REGULAR weight (lighter than the bold win/loss titles), solid black on
  // the cyan face. High enough contrast that stroke + shadow aren't needed.
  return {
    fontFamily: FONTS.fancyRegular,
    fontSize: `${fontSize}px`,
    color: '#000000',
  };
}

function drawCyanPill(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  radius: number,
): void {
  const halfW = w / 2;
  const halfH = h / 2;

  // Clean light-blue pill: a blue body gradient inside a blue rim. No pale inner
  // ring and no top sheen, so there is no whiteish glow anywhere on the face.
  const rimW = Math.max(3, Math.round(h * 0.05));

  // subtle dark drop shadow
  g.fillStyle(COLORS.abyssBlue, 0.30);
  g.fillRoundedRect(-halfW + 2, -halfH + 5, w, h, radius);

  // blue rim
  g.fillStyle(COLORS.buttonRim, 1);
  g.fillRoundedRect(-halfW, -halfH, w, h, radius);

  // body: light blue (top) easing into a deeper blue (base)
  g.fillGradientStyle(COLORS.buttonFace, COLORS.buttonFace, COLORS.buttonFaceDeep, COLORS.buttonFaceDeep, 1, 1, 1, 1);
  g.fillRoundedRect(-halfW + rimW, -halfH + rimW, w - rimW * 2, h - rimW * 2, Math.max(radius - rimW, 2));
}

function drawDangerPill(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  radius: number,
): void {
  const halfW = w / 2;
  const halfH = h / 2;

  // Drop shadow
  g.fillStyle(COLORS.midnightDepth, 0.55);
  g.fillRoundedRect(-halfW + 2, -halfH + 6, w, h, radius);

  // Coral body
  g.fillGradientStyle(COLORS.rubyCoralDeep, COLORS.rubyCoralDeep, COLORS.rubyCoral, COLORS.rubyCoral, 1, 1, 1, 1);
  g.fillRoundedRect(-halfW, -halfH, w, h, radius);

  // Top sheen
  g.fillStyle(COLORS.rubyCoralLight, 0.18);
  g.fillRoundedRect(-halfW + 6, -halfH + 4, w - 12, Math.floor(h * 0.42), Math.max(radius - 4, 4));

  // Rim (outer thick + inner hairline)
  g.lineStyle(3, COLORS.rubyCoral, 0.85);
  g.strokeRoundedRect(-halfW, -halfH, w, h, radius);
  g.lineStyle(1, COLORS.rubyCoralLight, 0.40);
  g.strokeRoundedRect(-halfW + 5, -halfH + 5, w - 10, h - 10, Math.max(radius - 4, 4));
}
